import React from 'react';
import ReactDOM from 'react-dom/client';

// 1. Base Design System
import './styles/design-system.css';

// 2. Platform-Specific Variables
import './styles/platform-specific.css';

// 3-13. Page-Specific Styles (in dependency order)
import './styles/layout.css';
import './styles/forms.css';
import './styles/auth.css';
import './styles/profile.css';
import './styles/order-management.css';
import './styles/user-management.css';
import './styles/landing.css';
import './styles/admin.css';

// 13. Utility Classes
import './styles/utilities.css';

// 14. Component Token Overrides (LAST!)
import './styles/component-tokens.css';

// 15. Temporary Migration Files
import './styles/form-types.css';

// 16. App-Specific Styles
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { getPlatformClass, getPlatformType } from './utils/platformUtils';
import CrashlyticsService from './services/CrashlyticsService.js';
// Import i18n configuration
import './i18n';

// CSS Loading Order (CORRECTED - Fixed cascade issues):
// 1. design-system.css - Global design tokens and base styles
// 2. platform-specific.css - Platform overrides (loads early for proper cascade)
// 3. layout.css - Navigation and layout components
// 4. forms.css - Form styles and components
// 5. auth.css - Authentication and landing page styles
// 6. profile.css - Profile page styles
// 7. user-management.css - User management styles
// 8. landing.css - Landing page styles
// 9. admin.css - Admin styles
// 10. utilities.css - Utility classes and helper styles
// 11. component-tokens.css - Component-specific tokens and CSS classes (LAST!)
// 12. form-types.css - Form type variants (temporary migration)
// 13. index.css - App-specific styles last

// Global error handler for unhandled errors
window.onerror = function(message, source, lineno, colno, error) {
  // Log to Crashlytics if available
  try {
    const crashlyticsService = CrashlyticsService.getInstance();
    
    // Add context about global error
    try {
      crashlyticsService.setCustomKeys([
        { key: 'error_source', value: source || 'unknown' },
        { key: 'error_line', value: lineno?.toString() || 'unknown' },
        { key: 'error_column', value: colno?.toString() || 'unknown' },
        { key: 'current_url', value: window.location.href },
        { key: 'platform_type', value: getPlatformType() }
      ]);
    } catch (contextError) {
      // Silently handle if context setting fails
    }
    
    crashlyticsService.recordError(error || new Error(message), `Global Error: ${source}:${lineno}`);
  } catch (crashlyticsError) {
    // Silently handle Crashlytics logging failures
  }
  
  return false;
};

// Compute platform class once
const platformClass = getPlatformClass();

// Add platform class to #root for global CSS targeting
const rootElement = document.getElementById('root');
if (rootElement) {
  rootElement.classList.add(platformClass);
}

// Add platform class to <body> for native and PWA mobile styling
if (platformClass === 'platform-ios') {
  document.body.classList.add('platform-ios');
} else if (platformClass === 'platform-ios-pwa') {
  document.body.classList.add('platform-ios-pwa');
} else if (platformClass === 'platform-android') {
  document.body.classList.add('platform-android');
} else if (platformClass === 'platform-android-pwa') {
  document.body.classList.add('platform-android-pwa');
}

// --- PATCH: Force re-check and re-apply platform class after delay ---
// setTimeout(() => {
//   const newClass = getPlatformClass();
//   if (rootElement) {
//     rootElement.classList.remove('platform-web', 'platform-ios', 'platform-android', 'platform-ios-pwa', 'platform-android-pwa');
//     rootElement.classList.add(newClass);
//   }
//   document.body.classList.remove('platform-web', 'platform-ios', 'platform-android', 'platform-ios-pwa', 'platform-android-pwa');
//   if (newClass === 'platform-ios' || newClass === 'platform-ios-pwa') {
//     document.body.classList.add('platform-ios');
//     if (newClass === 'platform-ios-pwa') document.body.classList.add('platform-ios-pwa');
//   } else if (newClass === 'platform-android' || newClass === 'platform-android-pwa') {
//     document.body.classList.add('platform-android');
//     if (newClass === 'platform-android-pwa') document.body.classList.add('platform-android-pwa');
//   } else {
//     document.body.classList.add('platform-web');
//   }
// }, 500);

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Service Worker Management - Clean up problematic old service workers
if ('serviceWorker' in navigator) {
  // Unregister any existing problematic service workers to fix white screen issues
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      // Only unregister the old caching service worker, keep Firebase messaging worker
      if (registration.scope && registration.active?.scriptURL.includes('serviceWorker.js')) {
        registration.unregister().then(success => {
          if (success) {
            // Clear all caches to ensure fresh start
            caches.keys().then(cacheNames => {
              return Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
            });
          }
        });
      }
    });
  }).catch(error => {
    // Silently handle service worker management errors
  });
}

